import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import useKeydown from "@hooks/useKeydown";
import PATHS from "@utils/paths";
import MenuItem from "./MenuItem";
import { useTranslation } from "react-i18next";
import { toast } from "@ino-ui/tv";
import { Modal, InoProtectInput } from "@ino-ui/tv";

import SvgApps from "@assets/images/menu/SvgApps";
import SvgLive from "@assets/images/menu/SvgLive";
import SvgMovies from "@assets/images/menu/SvgMovies";
import SvgSettings from "@assets/images/menu/SvgSettings";
import bgApps from "@assets/images/menu/bg_apps.png";
import bgLive from "../../../public/image/bg_live.png";
import bgMovies from "@assets/images/menu/bg_movies.png";
import bgSettings from "@assets/images/menu/bg_settings.png";
import SvgMore from "@assets/images/menu/SvgMore";
import { selectCtrl, selectIsUpdateModalOpen } from "@app/global";
import { validateAppPin } from "@server/requests";
import { customKeyboard } from "@pages/movieInfo/components/MovieActions";
import {
  currentPlatform,
  currentPlatformConfig,
  isLauncherMode,
  PLATFORMS,
} from "@platform/index";
import { launchApp } from "@platform/appLauncher";

import "./menuItems.scss";
import { ENV } from "@config/env";

export default function MenuItems({
  apps,
  isLauncher,
  onShowLogoutModal,
  isLogoutModalOpen,
}) {
  const location = useLocation();
  const isUpdateModalOpen = useSelector(selectIsUpdateModalOpen);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const ctrl = useSelector(selectCtrl);
  const [section, setSection] = useState("menu");
  const [isLauncherMode, setIsLauncherMode] = useState(true);

  // Check if launcher mode is disabled
  useEffect(() => {
    const appSettings = localStorage.getItem("app_settings");
    if (appSettings) {
      const settings = JSON.parse(appSettings);
      setIsLauncherMode(settings.launcher !== false);
    }
  }, []);

  const [list, setList] = useState([
    {
      id: 0,
      title: t("Live Action"),
      icon: <SvgLive />,
      bg_image: bgLive,
      path: PATHS.LIVE,
    },
    {
      id: 2,
      title: t("Info"),
      icon: <SvgSettings />,
      bg_image: bgSettings,
      path: PATHS.SETTINGS,
    },
  ]);

  const [activeIndex, setActiveIndex] = useState(0);
  const [show, setShow] = useState(false);
  const [isAppPinModalOpen, setIsAppPinModalOpen] = useState(false);
  const [appToLaunch, setAppToLaunch] = useState(null);

  const onClick = useCallback((path) => {
    navigate(path);
  }, []);

  useEffect(() => {
    // Show menu items based on platform capabilities
    if (currentPlatformConfig.supportsAppLaunching) {
      const menuItems = [
        // {
        //   id: 1,
        //   title: t("Movies"),
        //   icon: <SvgMovies />,
        //   bg_image: bgMovies,
        //   path: PATHS.MOVIES,
        // },
        // {
        //   id: 3,
        //   title: t("Apps"),
        //   icon: <SvgApps />,
        //   bg_image: bgApps,
        //   path: PATHS.APPS,
        // },
      ];

      console.log(ENV.SHOW_VOD.toString(), "--env show vod---");

      if (ENV.SHOW_VOD.toString() === "true") {
        menuItems.push({
          id: 1,
          title: t("Movies"),
          icon: <SvgMovies />,
          bg_image: bgMovies,
          path: PATHS.MOVIES,
        });
      }

      const sortedList = [...list, ...menuItems].sort((a, b) => a.id - b.id);
      setList(sortedList);
    }
  }, []);

  const handleAppLaunch = async (app) => {
    if (!currentPlatformConfig.supportsAppLaunching) {
      toast.error("App launching not supported on this platform", {
        duration: 3000,
      });
      return;
    }

    if (app.is_protected) {
      setAppToLaunch(app);
      setIsAppPinModalOpen(true);
    } else {
      const success = await launchApp(app);
      if (!success) {
        toast.error("Failed to launch app", { duration: 3000 });
      }
    }
  };

  const handleAppPinSubmit = async (pin) => {
    try {
      const response = await validateAppPin({ app_pin: pin });
      const parsedResponse = JSON.parse(response);

      if (!parsedResponse.error) {
        const success = await launchApp(appToLaunch);
        if (!success) {
          toast.error("Failed to launch app", { duration: 3000 });
        }
        setIsAppPinModalOpen(false);
        setAppToLaunch(null);
      } else {
        toast.error(parsedResponse.message, { duration: 3000 });
      }
    } catch (err) {
      console.log(err);
      toast.error("Failed to validate PIN", { duration: 3000 });
    }
  };

  // Update keydown handler for grid layout
  useKeydown({
    isActive:
      !show &&
      !isUpdateModalOpen &&
      ctrl !== "blocked" &&
      !isAppPinModalOpen &&
      !isLogoutModalOpen,

    left: () => {
      if (section === "menu") {
        if (activeIndex === 0) return;
        setActiveIndex(activeIndex - 1);
      } else {
        if (activeIndex === 0) return;
        setActiveIndex(activeIndex - 1);
      }
    },

    right: () => {
      if (section === "menu") {
        if (ENV.SHOW_VOD.toString() === "true") {
          if (activeIndex > 0) return;
        }
        if (activeIndex === allItems.length - 1) return;
        setActiveIndex(activeIndex + 1);
      } else {
        if (activeIndex === apps.length - 1) return;
        setActiveIndex(activeIndex + 1);
      }
    },

    up: () => {
      // If we're in the apps section, go back to menu

      if (ENV.SHOW_VOD.toString() === "true") {
        if (activeIndex === 2) {
          setActiveIndex(0);
          return;
        }
      }

      if (section === "apps") {
        setSection("menu");
        setActiveIndex(0);
      }
    },

    down: () => {
      // Allow going down if in launcher mode and there are apps available

      if (ENV.SHOW_VOD.toString() === "true") {
        setActiveIndex(2);
        return;
      }

      if (
        isLauncher &&
        apps?.length > 0 &&
        currentPlatformConfig.supportsAppLaunching
      ) {
        if (section === "menu") {
          setSection("apps");
          setActiveIndex(0);
        }
      }
    },

    ok: () => {
      if (section === "menu") {
        onClick(allItems[activeIndex].path);
      } else {
        if (apps.length > 5 && activeIndex === 5) {
          navigate("/apps");
        } else {
          const item = apps[activeIndex];
          if (item.isMoreApps) {
            navigate(PATHS.APPS);
          } else if (item?.app_id) {
            handleAppLaunch(item);
          }
        }
      }
    },

    back: () => {
      if (!isLauncherMode) {
        // Show logout modal when launcher is false
        if (onShowLogoutModal) {
          onShowLogoutModal();
        }
      } else {
        // Normal behavior for launcher mode
        if (currentPlatformConfig.supportsBackButton) {
          currentPlatformConfig.goToHome();
        }
      }
    },
  });

  const onMouseEnter = useCallback((index) => setActiveIndex(index), []);

  const allItems = useMemo(() => list, [list]);

  return (
    <>
      <Modal
        isOpen={isAppPinModalOpen}
        onClose={() => {
          setIsAppPinModalOpen(false);
          setAppToLaunch(null);
        }}
        onCancel={() => {}}
        onOk={() => {}}
        size="full"
      >
        <InoProtectInput
          title="Enter PIN"
          isActive={isAppPinModalOpen}
          count={6}
          isAsterisk={true}
          isOpenKeyboard={true}
          onComplete={handleAppPinSubmit}
          customType={customKeyboard}
          onChange={(value) => {
            console.log(value);
          }}
          clearOnComplete={true}
          onCancel={() => {
            setIsAppPinModalOpen(false);
            setAppToLaunch(null);
          }}
        />
      </Modal>
      <div className="menu-list_wrapper">
        <div className="menu-section">
          {allItems.map((item, index) => (
            <MenuItem
              key={item.id}
              item={item}
              isActive={activeIndex === index && section === "menu"}
              index={index}
              onClick={onClick}
              onMouseEnter={onMouseEnter}
            />
          ))}
        </div>

        {/* Show apps list only if platform supports app launching and we're in launcher mode */}
        {isLauncher &&
          apps &&
          apps.length > 0 &&
          currentPlatformConfig.supportsAppLaunching && (
            <div className="apps-section">
              {apps.slice(0, 5).map((app, index) => (
                <MenuItem
                  key={`app-${index}`}
                  item={{
                    id: `app-${index}`,
                    title: app.name,
                    icon: (
                      <img
                        src={app.icon}
                        alt={app.name}
                        style={{ width: "100%", height: "100%" }}
                      />
                    ),
                    bg_image: app.icon || bgApps,
                    path: app.app_id,
                    isApp: true,
                  }}
                  isActive={activeIndex === index && section === "apps"}
                  index={index}
                  onClick={() => handleAppLaunch(app)}
                  onMouseEnter={onMouseEnter}
                />
              ))}
              {apps.length > 5 && (
                <MenuItem
                  key="more-apps"
                  item={{
                    id: "more-apps",
                    title: t("More Apps"),
                    icon: <SvgMore />,
                    bg_image: bgApps,
                    path: PATHS.APPS,
                    isMoreApps: true,
                  }}
                  isActive={activeIndex === 5 && section === "apps"}
                  index={5}
                  onClick={() => navigate(PATHS.APPS)}
                  onMouseEnter={onMouseEnter}
                />
              )}
            </div>
          )}
      </div>
    </>
  );
}
